// Detects an open→resolved transition in the live query result and drives
// the resolve micro-interaction + auto-advance (docs/06-ux-rules.md
// "Auto-advance", "Resolve animation"; 11-chassis-plan.md §2 PR 04
// "Container wiring"). Extracted out of `FeedbackInbox/index.tsx` — that
// component already owns 6+ hooks (repo convention: 3+ hooks in a scene →
// extract to a co-located `useXxx.ts`).
//
// Why detection is done here, generically, rather than from a specific
// "resolve" button handler: the resolve MUTATION (PR 05's `useInboxActions`,
// PR 06's composer) lands in later chassis PRs, but RTK Query's optimistic
// patch (already wired in `src/api/feedbackInbox.ts`, PR 01) flips an
// item's `status` in the cached list the instant ANY resolve call starts —
// regardless of which future component fires it. Watching the query
// result for the transition, instead of coupling to a specific caller,
// means this container-level wiring doesn't need to change again once
// PR 05/06 land.
//
// `stillShows`: the only filter dimension a resolve can change is
// `status` — kind/rating/location/search never change when an item
// resolves. So "does this row still belong under the active filter" is
// exactly `statusFilter !== 'open'` (Status = All/Resolved keeps every row,
// regardless of its own status; Status = Open drops anything that just
// became resolved).
import type {
  FeedbackInboxItem,
  InboxStatus,
  ItemStatus,
} from 'api/feedbackInbox.types'
import { buildTranslate } from 'locales'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ResolveAnimationPhase } from './InboxList/useResolveAnimation'
import { useResolveAnimation } from './InboxList/useResolveAnimation'

const t = buildTranslate('feedback_inbox')

// One leaving row's lifecycle from detection to either a completed advance
// or a rollback (AI-1351 design §2). Only rows leaving Status=Open get a
// session — a flash/pulse row (stays under the filter) never needs one.
interface ResolveSession {
  exited: boolean // exit animation finished; row dropped from displayItems
  confirmed: boolean // success seen (confirmResolved; legacy mode also: row left items)
  exitIndex: number // displayItems index at exit, for picking the neighbour
  advancedTo: string | null // where selection went, so a rollback can undo it
}

export interface UseResolveAutoAdvanceOptions {
  items: FeedbackInboxItem[]
  statusFilter: InboxStatus
  selectedId: string | null
  onSelect: (id: string) => void
  // The resolved row that left was the only item on the current page —
  // caller refetches (via `invalidatesTags`, already automatic) and should
  // select whatever lands first once the new page arrives.
  onPageEmptied: () => void
  announce: (text: string) => void
  pulseResolvedCount: () => void
  // Open look-back target (`params.lookback`). While a look-back is open a
  // resolve never moves selection, announces, or calls onPageEmptied — the
  // container announces "Resolved" itself (S4 §3.2 G1/G2/G5).
  lookbackId?: string | null
  // Opt-in: hold the auto-advance until the caller calls
  // `confirmResolved(id)` (mutation success) — a row simply dropping out
  // of `items` is NOT treated as confirmation here, since that can also
  // mean the page/filter/search changed while the resolve was still in
  // flight. Omitted = pre-AI-1351 behaviour: advance the moment the exit
  // animation ends.
  awaitResolveConfirmation?: boolean
}

export interface UseResolveAutoAdvanceResult {
  // `items`, plus any row still mid-exit-animation that a refetch has
  // already dropped from the live query result — kept around so the swipe-
  // out animation completes visually regardless of backend refetch timing.
  displayItems: FeedbackInboxItem[]
  getAnimationPhase: (itemId: string) => ResolveAnimationPhase
  // Success-only signal from the resolve mutation. Releases a held advance
  // (`awaitResolveConfirmation`); a no-op for an id with no session (e.g. a
  // look-back target that was never on the current page).
  confirmResolved: (itemId: string) => void
  // Failure-only signal from the resolve mutation's catch path (mirrors
  // `confirmResolved`). Under `awaitResolveConfirmation` this is the ONLY
  // thing that cancels a session's exit + restores selection — a bare
  // resolved→open transition can also be a stale refetch racing an
  // in-flight resolve of the same row, not a genuine rollback.
  rollbackResolved: (itemId: string) => void
}

export function useResolveAutoAdvance({
  items,
  statusFilter,
  selectedId,
  onSelect,
  onPageEmptied,
  announce,
  pulseResolvedCount,
  lookbackId,
  awaitResolveConfirmation,
}: UseResolveAutoAdvanceOptions): UseResolveAutoAdvanceResult {
  const [displayItems, setDisplayItems] = useState<FeedbackInboxItem[]>(items)
  const prevStatusRef = useRef<Record<string, ItemStatus>>({})
  const sessionsRef = useRef(new Map<string, ResolveSession>())
  // Read via refs inside `onExited` so it always sees the LATEST
  // `selectedId`/`onSelect`/etc. without forcing `useResolveAnimation` to
  // re-create its internal callback refs every render.
  const selectedIdRef = useRef(selectedId)
  selectedIdRef.current = selectedId
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const onPageEmptiedRef = useRef(onPageEmptied)
  onPageEmptiedRef.current = onPageEmptied
  const announceRef = useRef(announce)
  announceRef.current = announce
  const lookbackIdRef = useRef(lookbackId)
  lookbackIdRef.current = lookbackId
  // Read by `rollbackResolved` to restore a row `onExited` already dropped
  // from `displayItems` — an external caller (the mutation's catch path)
  // gets no free reconciliation pass the way the effect below does.
  const itemsRef = useRef(items)
  itemsRef.current = items
  const awaitConfirmationRef = useRef(awaitResolveConfirmation)
  awaitConfirmationRef.current = awaitResolveConfirmation

  // Mirrors the committed `displayItems` so `onExited` can pick the
  // auto-advance target WITHOUT reading it inside the state updater.
  // Updaters run during the render phase and must be pure (.claude/docs/
  // conventions.md "Never nest state updates inside state updater
  // functions") — the side effects below reach `history.push` via
  // `onSelect` → `pushParams`, so running them in there pushed router
  // history mid-render, with undefined ordering against the `displayItems`
  // commit. Synced in an effect rather than inside either updater because
  // `displayItems` is written from two places (here and the detection
  // effect below), and mutating a ref inside an updater is banned by the
  // same rule.
  const displayItemsRef = useRef(displayItems)
  useEffect(() => {
    displayItemsRef.current = displayItems
  }, [displayItems])

  // Reads refs only, never mutates a session — safe to call from `onExited`
  // (inside a state-updater-adjacent callback) or from `confirmResolved`
  // (an external caller). A look-back open, or the user having since
  // selected a different row, means there is nothing to advance.
  const advanceFrom = useCallback(
    (itemId: string, index: number): string | null => {
      if (selectedIdRef.current !== itemId || lookbackIdRef.current) {
        return null
      }

      const remaining = displayItemsRef.current.filter(
        item => item.id !== itemId
      )
      const candidate = remaining[index] ?? remaining[index - 1] ?? null

      if (!candidate) {
        onPageEmptiedRef.current()
        return null
      }

      onSelectRef.current(candidate.id)
      announceRef.current(
        t('actions.resolved_announcement', {
          name: candidate.customer.name ?? t('list.unknown_guest'),
        })
      )
      return candidate.id
    },
    []
  )

  const onExited = useCallback(
    (itemId: string) => {
      // The REMOVAL stays a composing updater: two rows whose exit
      // animations land in the same tick must each drop only themselves.
      // Setting a snapshot-derived array instead would let the second
      // removal resurrect the first row once React 18's automatic batching
      // collapses the two commits (see the same-tick test in this hook's
      // spec).
      setDisplayItems(current => current.filter(item => item.id !== itemId))

      const session = sessionsRef.current.get(itemId)
      if (!session) return

      session.exited = true
      // Safe to read the mirror here: `onExited` only ever fires from a
      // `setTimeout` or from inside an effect (useResolveAnimation), so a
      // commit — and this hook's sync effect — has always landed first.
      session.exitIndex = displayItemsRef.current.findIndex(
        item => item.id === itemId
      )

      if (awaitConfirmationRef.current && !session.confirmed) return

      session.advancedTo = advanceFrom(itemId, session.exitIndex)
      if (session.confirmed) sessionsRef.current.delete(itemId)
    },
    [advanceFrom]
  )

  const confirmResolved = useCallback(
    (itemId: string) => {
      const session = sessionsRef.current.get(itemId)
      if (!session) return

      session.confirmed = true
      if (!session.exited) return

      if (awaitConfirmationRef.current) {
        session.advancedTo = advanceFrom(itemId, session.exitIndex)
      }
      sessionsRef.current.delete(itemId)
    },
    [advanceFrom]
  )

  const { getPhase, startResolve, cancel } = useResolveAnimation({
    onExited,
    onPulseResolved: pulseResolvedCount,
  })

  // Shared by the legacy-mode auto-detected rollback (below) and the
  // gated-mode explicit `rollbackResolved` call: stop the animation, drop
  // the session, and undo the auto-advance if it's still the live selection.
  const rollbackResolved = useCallback(
    (itemId: string) => {
      cancel(itemId)
      const session = sessionsRef.current.get(itemId)
      if (!session) return
      sessionsRef.current.delete(itemId)

      if (session.exited) {
        // `onExited` already dropped the row from `displayItems` — unlike
        // the legacy auto-detected path (which rides the items-effect's own
        // reconciliation below), a rollback discovered this late has no
        // later render to put the row back, so it does so itself.
        const restored = itemsRef.current.find(i => i.id === itemId)
        if (restored) {
          setDisplayItems(current => {
            if (current.some(i => i.id === itemId)) return current
            const next = [...current]
            const index = Math.max(0, Math.min(session.exitIndex, next.length))
            next.splice(index, 0, restored)
            return next
          })
        }
      }

      if (
        session.advancedTo !== null &&
        session.advancedTo === selectedIdRef.current
      ) {
        // Restore selection only if we moved it and the user hasn't
        // moved since.
        onSelectRef.current(itemId)
      }
    },
    [cancel]
  )

  useEffect(() => {
    const prevStatuses = prevStatusRef.current
    items.forEach(item => {
      const prevStatus = prevStatuses[item.id]
      if (prevStatus === 'open' && item.status === 'resolved') {
        const leaving = statusFilter === 'open'
        if (leaving) {
          sessionsRef.current.set(item.id, {
            exited: false,
            confirmed: false,
            exitIndex: -1,
            advancedTo: null,
          })
        }
        startResolve(item.id, !leaving)
      } else if (prevStatus === 'resolved' && item.status === 'open') {
        const session = sessionsRef.current.get(item.id)
        if (session && !session.confirmed && awaitConfirmationRef.current) {
          // Gated + unconfirmed: only `rollbackResolved` (the mutation's own
          // catch path) counts as a rollback here — seeing it in this effect
          // isn't proof, since a stale refetch racing an in-flight resolve
          // of this same row looks identical. Once confirmed, the mutation
          // already committed server-side, so a later resolved→open can
          // only be a genuine reversal (e.g. Unresolve) — fall through.
          return
        }
        // A rollback (patch.undo()) and a deliberate unresolve look
        // identical here: either way the resolve visuals are void.
        rollbackResolved(item.id)
      }
    })
    prevStatusRef.current = Object.fromEntries(
      items.map(item => [item.id, item.status])
    )

    // Legacy: a row leaving `items` means success (the only cleanup path
    // there). Gated: that can also mean the page/filter/search changed
    // mid-flight, so only an unconfirmed session is abandoned here — a
    // confirmed one is left for `onExited`/`rollbackResolved` to finish.
    const liveIds = new Set(items.map(item => item.id))
    Array.from(sessionsRef.current.keys()).forEach(id => {
      if (liveIds.has(id)) return
      if (awaitConfirmationRef.current) {
        if (!sessionsRef.current.get(id)?.confirmed) {
          sessionsRef.current.delete(id)
        }
        return
      }
      confirmResolved(id)
    })

    // The Open list can only contain a resolved row via an optimistic
    // patch. Once it is no longer animating out it must not show, however
    // it got there — this is what makes unresolve, the resurrection bug,
    // and the reduced-motion re-add all work with one rule.
    const hiddenIds = new Set<string>()
    if (statusFilter === 'open') {
      items.forEach(item => {
        if (item.status !== 'resolved') return
        const session = sessionsRef.current.get(item.id)
        const animatingOut = !!session && !session.exited
        if (!animatingOut) hiddenIds.add(item.id)
      })
    }

    setDisplayItems(current => {
      // A still-exiting row that a refetch has already dropped from `items`
      // is spliced back in at its LAST-KNOWN index (its position in
      // `current`), not appended to the end — an append would make the row
      // visibly jump to the bottom of the list if the refetch lands mid-
      // animation, before the swipe-out has a chance to play from where the
      // row actually was.
      // Only the leaving phases: a `flash` row never leaves the list, so one
      // a page/search/filter change dropped must stay dropped.
      const next = items.filter(item => !hiddenIds.has(item.id))
      current.forEach((item, index) => {
        if (liveIds.has(item.id)) return
        const phase = getPhase(item.id)
        if (phase !== 'checking' && phase !== 'exiting') return
        next.splice(Math.min(index, next.length), 0, item)
      })
      return next
    })
    // `getPhase`/`startResolve`/`cancel`/`confirmResolved`/`rollbackResolved`
    // are fresh functions every render (from `useResolveAnimation`'s own
    // state, or closing over refs) — only a real `items`/`statusFilter`
    // change should re-run detection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, statusFilter])

  return {
    displayItems,
    getAnimationPhase: getPhase,
    confirmResolved,
    rollbackResolved,
  }
}
